import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button3D } from '@/components/ui/button-3d';
import { FileDown, Loader2, Save, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import type { GuestQuote } from '@/types/database';
import { formatBRL } from '@/lib/financeLabels';
import {
  getGuestQuote,
  isImportDismissed,
  dismissImportForNow,
  discardGuestQuote,
  importGuestQuote,
} from '@/lib/guestQuoteImport';
import { toast } from 'sonner';

type Choice = 'import' | 'keep' | 'delete' | null;

/**
 * After a visitor logs in, ask what to do with the quote they built while
 * signed out: import it into the account, keep it saved locally without
 * importing, or discard it. Mounted inside CompanyProvider so it can resolve the
 * active company for a company-scoped (RLS-safe) import.
 */
export function GuestQuoteImportPrompt() {
  const { user } = useAuth();
  const { activeCompany, loading: companyLoading } = useCompany();
  const [guestQuote, setGuestQuote] = useState<GuestQuote | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<Choice>(null);

  // Decide whether to prompt once the user is logged in. Wait for the company
  // list to settle so the import lands in a writable company.
  useEffect(() => {
    if (!user || companyLoading) {
      setOpen(false);
      return;
    }

    const guest = getGuestQuote();
    if (guest && !isImportDismissed(guest)) {
      setGuestQuote(guest);
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [user, companyLoading]);

  const handleImport = async () => {
    if (!user || busy) return;
    setBusy('import');
    try {
      const ok = await importGuestQuote({
        userId: user.id,
        companyId: activeCompany?.id ?? null,
      });
      if (ok) {
        setOpen(false);
      }
      // On failure importGuestQuote surfaces its own toast; keep the dialog open
      // so the user can retry or pick another option.
    } finally {
      setBusy(null);
    }
  };

  const handleKeep = () => {
    if (busy) return;
    dismissImportForNow(guestQuote);
    setOpen(false);
    toast.info('Orçamento de visitante mantido no dispositivo.');
  };

  const handleDelete = () => {
    if (busy) return;
    discardGuestQuote();
    setOpen(false);
    toast.info('Orçamento de visitante excluído.');
  };

  if (!guestQuote) return null;

  const itemCount = guestQuote.items?.length ?? 0;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !busy) handleKeep(); }}>
      <DialogContent
        className="sm:max-w-md [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="text-center sm:text-left space-y-2">
          <div className="mx-auto sm:mx-0 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950/50 text-emerald-600">
            <FileDown className="h-6 w-6" />
          </div>
          <DialogTitle className="text-xl font-bold">
            Importar orçamento de visitante?
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Você criou um orçamento antes de entrar na conta. O que deseja fazer com ele?
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border bg-muted/40 p-3.5 text-sm">
          <p className="font-semibold text-foreground truncate">
            {guestQuote.title?.trim() || guestQuote.client_name || 'Orçamento sem título'}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {itemCount} {itemCount === 1 ? 'item' : 'itens'} · {formatBRL(guestQuote.total || 0)}
          </p>
        </div>

        <div className="flex flex-col gap-2 pt-1">
          <Button3D
            onClick={handleImport}
            disabled={!!busy}
            className="w-full"
          >
            {busy === 'import' ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Importando...
              </>
            ) : (
              <>
                <FileDown className="mr-2 h-4 w-4" />
                Sim, importar para minha conta
              </>
            )}
          </Button3D>

          <Button3D
            color="gray"
            onClick={handleKeep}
            disabled={!!busy}
            className="w-full"
          >
            <Save className="mr-2 h-4 w-4" />
            Não importar, manter salvo
          </Button3D>

          <Button3D
            color="red"
            onClick={handleDelete}
            disabled={!!busy}
            className="w-full"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Não importar e excluir
          </Button3D>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default GuestQuoteImportPrompt;
